/**
 * The library as the app holds it: the person's folders and files, the uploads in flight, and the control that
 * every upload opens. One store, so the Assets page and the control always agree, and an upload carries on while
 * the person moves around the app.
 */
import { useSyncExternalStore } from "react";
import type { LibraryAsset, LibraryFileKind, LibraryFolder, LibraryFolderPatch } from "../../shared/types";
import { api, ApiError } from "../lib/api";
import { pollTriesFor } from "../lib/noteUpload";
import { ALL, FITS, type Fit, type FitName, fitOf, guessKind, hasCopy, uploadTarget } from "./library";
import { type UploadStage, uploadToLibrary } from "./upload";

/** A file going up: it shows as a tile with its stage until the kept file replaces it. */
export interface Upload {
  id: string;
  name: string;
  kind: LibraryFileKind;
  /** The device file's size, which with its name tells a second pick of the same file. */
  size: number;
  folderId: string | null;
  stage: UploadStage;
  pct: number;
  /** The device's own copy, so a picture or video shows while it goes up. */
  preview: string | null;
  /** Where it was started from, so a finished upload is picked in that control only. */
  from: "pick" | "page";
  error: string | null;
}

interface PickerOptions {
  /** What fits the place: a name from FITS or a custom fit. */
  fits?: FitName | Fit;
  title?: string;
  sub?: string;
  /** The main button's words: "Use this" unless the place says otherwise. */
  cta?: string;
  /** The folder it opens on, when the place knows better than the fit. */
  at?: string;
  /** Files dropped or pasted on the place, uploaded at once. */
  incoming?: File[];
  onPick: (asset: LibraryAsset) => void;
  /** Closed without picking. */
  onCancel?: () => void;
}

export interface PickerState {
  key: number;
  fit: Fit;
  title: string;
  sub: string;
  cta: string;
  folder: string;
  q: string;
  sel: string | null;
  /** The file an upload just picked, said in the footer. */
  auto: string | null;
  /** The file being got ready (copied in the first time it is used) before the place gets it. */
  using: string | null;
  /** Why the last pick didn't reach the place. */
  error: string | null;
  all: boolean;
  newFolder: boolean;
  onPick: (asset: LibraryAsset) => void;
  onCancel?: () => void;
}

export interface LibraryState {
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  folders: LibraryFolder[];
  assets: LibraryAsset[];
  /** Files deleted in the last 30 days, kept as the library sends them. */
  deleted: LibraryAsset[];
  uploads: Upload[];
  /** Files that just arrived, marked "Just added" for a few seconds. */
  fresh: string[];
  picker: PickerState | null;
}

const FRESH_MS = 6000;

let state: LibraryState = { status: "idle", error: null, folders: [], assets: [], deleted: [], uploads: [], fresh: [], picker: null };
const listeners = new Set<() => void>();
let seq = 0;

function set(next: Partial<LibraryState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function libraryState(): LibraryState {
  return state;
}

/**
 * True while the library cannot be read, or has not been yet: counts would say 0 for files that are only out of sight,
 * and adding to a library that cannot be reached would fail.
 */
export function libraryBlind(s: Pick<LibraryState, "status" | "folders" | "assets">): boolean {
  return s.status === "error" || (s.status !== "ready" && !s.folders.length && !s.assets.length);
}

export function useLibrary(): LibraryState {
  return useSyncExternalStore(subscribe, libraryState, libraryState);
}

/** Read the library from the server; again only when asked or after a failure. */
export async function loadLibrary(force = false): Promise<void> {
  if (state.status === "loading" || (state.status === "ready" && !force)) return;
  set({ status: "loading", error: null });
  try {
    const lib = await api.library.list();
    set({ status: "ready", folders: lib.folders, assets: lib.assets, deleted: lib.deleted });
  } catch (e) {
    set({ status: "error", error: e instanceof Error ? e.message : "We couldn't open Assets." });
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** How often a copy still coming over is asked about. */
const COPY_POLL_MS = 1500;

function replaceAsset(a: LibraryAsset): void {
  set({ assets: state.assets.map((x) => (x.id === a.id ? a : x)) });
}

/**
 * The file a place gets for a pick: Flashroom's own copy of it, made the first time it is used and the same one after,
 * so deleting the library's file never breaks what uses it. A room's talk needs the copy to be all there; anything
 * else waits until it is playable. A room's replay is Flashroom's own and needs no copy.
 */
export async function useFile(a: LibraryAsset, wait: "whole" | "ready"): Promise<LibraryAsset> {
  if (hasCopy(a, wait)) return a;
  for (let i = 0, tries = pollTriesFor(a.size_bytes, COPY_POLL_MS); i < tries; i++) {
    const { asset, whole } = await api.library.use(a.id);
    replaceAsset(asset);
    if (asset.failed) throw new Error("That file could not be made playable. Try again.");
    if (whole && hasCopy(asset, wait)) return asset;
    await sleep(COPY_POLL_MS);
  }
  throw new Error("That is taking longer than usual. Try again in a minute.");
}

/** The files as they show: uploads in flight first, as tiles of their own. */
export function uploadsAsAssets(uploads: Upload[]): LibraryAsset[] {
  return uploads.map((u) => ({
    id: u.id,
    name: u.name,
    kind: u.kind,
    mime: "",
    size_bytes: 0,
    folder_id: u.folderId,
    source: "upload" as const,
    key: null,
    note_id: null,
    media_key: null,
    poster_key: null,
    video_id: null,
    webinar_id: null,
    url: u.preview,
    poster_url: null,
    ready: false,
    width: null,
    height: null,
    duration_sec: null,
    pages: null,
    created_at: Math.floor(Date.now() / 1000),
    uses: [],
  }));
}

function patchUpload(id: string, next: Partial<Upload>): void {
  set({ uploads: state.uploads.map((u) => (u.id === id ? { ...u, ...next } : u)) });
}

function markFresh(id: string): void {
  set({ fresh: [...state.fresh, id] });
  setTimeout(() => set({ fresh: state.fresh.filter((x) => x !== id) }), FRESH_MS);
}

/**
 * Upload files into a folder. Each file walks its tile through Uploading, Checking it, Putting it in the folder,
 * Ready. In the control, a finished file that fits is picked by itself: an upload is done to use it.
 * A file with the name and size of one still going up is not sent twice; the names left out are handed back so the
 * place can say so. A finished file can be uploaded again.
 */
export function uploadFiles(files: File[], folderId: string | null, from: "pick" | "page", onDone?: (a: LibraryAsset) => void): string[] {
  const already: string[] = [];
  for (const file of files) {
    if (state.uploads.some((u) => !u.error && u.name === file.name && u.size === file.size)) {
      already.push(file.name);
      continue;
    }
    const id = `up_${++seq}`;
    const kind = guessKind(file);
    const preview = (kind === "image" || kind === "video") && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
    set({ uploads: [{ id, name: file.name, kind, size: file.size, folderId, stage: 0, pct: 0, preview, from, error: null }, ...state.uploads] });
    const pickerKey = state.picker?.key ?? null;
    if (from === "pick" && state.picker) set({ picker: { ...state.picker, sel: null, auto: null } });
    uploadToLibrary(file, folderId, (p) => patchUpload(id, { stage: p.stage, pct: p.pct }))
      .then((asset) => {
        set({ uploads: state.uploads.filter((u) => u.id !== id), assets: [asset, ...state.assets.filter((a) => a.id !== asset.id)] });
        if (preview) URL.revokeObjectURL(preview);
        markFresh(asset.id);
        const p = state.picker;
        if (from === "pick" && p && p.key === pickerKey && p.fit.test(asset)) set({ picker: { ...p, sel: asset.id, auto: asset.id } });
        onDone?.(asset);
      })
      .catch((e: unknown) => patchUpload(id, { error: e instanceof Error ? e.message : "That upload didn't finish." }));
  }
  return already;
}

export async function createFolder(name: string, parentId: string | null): Promise<LibraryFolder> {
  const f = await api.library.folder({ name, parent_id: parentId });
  const recordings = state.folders.filter((x) => x.system === "recordings");
  set({ folders: [...state.folders.filter((x) => x.system !== "recordings"), f, ...recordings] });
  return f;
}

/**
 * Rename or move a folder. A folder's id is its path, so it changes for the folder, the folders inside it and every
 * file in them: the library is read again, and the folder's new id is handed back for whoever had it open.
 */
export async function changeFolder(id: string, patch: LibraryFolderPatch): Promise<string> {
  const to = await api.library.patchFolder(id, patch);
  await loadLibrary(true);
  return to.id;
}

/** Delete a folder with what is inside it; the library is read again, since the files left with it. */
export async function deleteFolder(id: string): Promise<void> {
  await api.library.removeFolder(id);
  await loadLibrary(true);
}

export async function moveFile(id: string, folderId: string | null): Promise<void> {
  const a = await api.library.patch(id, { folder_id: folderId });
  set({ assets: state.assets.map((x) => (x.id === id ? a : x)) });
}

/**
 * Delete and restore, together: the library's soft delete (kept 30 days) and its restore from Trash. A deleted file
 * leaves the listing at once and joins `deleted`; restoring it brings it back from there, the library's answer replacing it.
 */
export async function deleteFile(id: string): Promise<void> {
  const before = { assets: state.assets, deleted: state.deleted };
  const gone = state.assets.find((x) => x.id === id);
  set({ assets: before.assets.filter((x) => x.id !== id), ...(gone ? { deleted: [{ ...gone, deleted_at: Math.floor(Date.now() / 1000) }, ...before.deleted] } : {}) });
  try {
    await api.library.remove(id);
  } catch (e) {
    set(before);
    throw e;
  }
}

export interface RestoreResult {
  /** Each file restored, with the name it had in Trash: the library gives a new one when another file has it there. */
  restored: Array<{ was: string; asset: LibraryAsset }>;
  /** The names of files the library's sweep had already taken for good. */
  gone: string[];
  /** What the library said to a file it could not restore for another reason. */
  failed: string[];
  /** The folders as the library has them after, so the words name a folder that came back. */
  folders: LibraryFolder[];
}

/**
 * Restore files from Trash, one by one: each goes back to its own folder, and the library says what it is now called.
 * A file already swept for good leaves Trash with a word. The library is read again after, since a folder that was
 * deleted with a file comes back with it.
 */
export async function restoreFiles(ids: string[]): Promise<RestoreResult> {
  const out: RestoreResult = { restored: [], gone: [], failed: [], folders: state.folders };
  for (const id of ids) {
    const was = state.deleted.find((x) => x.id === id)?.name ?? "";
    try {
      const asset = await api.library.restore(id);
      out.restored.push({ was, asset });
      set({ assets: [asset, ...state.assets.filter((x) => x.id !== id)], deleted: state.deleted.filter((x) => x.id !== id) });
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        out.gone.push(was);
        set({ deleted: state.deleted.filter((x) => x.id !== id) });
      } else {
        out.failed.push(e instanceof Error ? e.message : "That file didn't come back.");
      }
    }
  }
  if (out.restored.length) await loadLibrary(true);
  return { ...out, folders: state.folders };
}

// ── the control ────────────────────────────────────────────────────────────────

/** Open the one control every upload opens, narrowed to what fits the place. */
export function openAssets(o: PickerOptions): void {
  const fit = fitOf(o.fits);
  const at = o.at ?? fit.at ?? ALL;
  const placed = (list: LibraryFolder[]) => list.find((f) => f.id === at || f.name.toLowerCase() === at.toLowerCase());
  set({
    picker: {
      key: ++seq,
      fit,
      title: o.title ?? "From Assets",
      sub: o.sub ?? "",
      cta: o.cta ?? "Use this",
      // Why by name too: the fit names a folder in a word ("images"), and a folder's id is its path.
      folder: placed(state.folders)?.id ?? ALL,
      q: "",
      sel: null,
      auto: null,
      using: null,
      error: null,
      all: false,
      newFolder: false,
      onPick: o.onPick,
      onCancel: o.onCancel,
    },
  });
  void loadLibrary().then(() => {
    const p = state.picker;
    if (!p) return;
    if (p.folder === ALL && at !== ALL) {
      const found = placed(state.folders);
      if (found) set({ picker: { ...p, folder: found.id } });
    }
    if (o.incoming?.length) uploadFiles(o.incoming, uploadTarget(state.folders, state.picker?.folder ?? ALL), "pick");
  });
}

export function updatePicker(next: Partial<PickerState>): void {
  if (state.picker) set({ picker: { ...state.picker, ...next } });
}

export function closePicker(picked = false): void {
  const p = state.picker;
  set({ picker: null });
  if (p && !picked) p.onCancel?.();
}

/** Use the picked file: the control closes and hands it to the place, once Flashroom's own copy is ready. */
export function pickSelected(): void {
  const p = state.picker;
  if (!p || p.using) return;
  const a = state.assets.find((x) => x.id === p.sel);
  if (!a || !p.fit.test(a)) return;
  const wait = p.fit === FITS.talk ? "whole" : "ready";
  if (hasCopy(a, wait)) {
    set({ picker: null });
    p.onPick(a);
    return;
  }
  set({ picker: { ...p, using: a.id, error: null } });
  useFile(a, wait)
    .then((own) => {
      if (state.picker?.key !== p.key) return;
      set({ picker: null });
      p.onPick(own);
    })
    .catch((e: unknown) => {
      if (state.picker?.key !== p.key) return;
      set({ picker: { ...state.picker, using: null, error: e instanceof Error ? e.message : "That didn't get ready. Try again." } });
    });
}

/** Tests only: start from an empty library. */
export function resetLibraryForTests(next: Partial<LibraryState> = {}): void {
  state = { status: "idle", error: null, folders: [], assets: [], deleted: [], uploads: [], fresh: [], picker: null, ...next };
  for (const l of listeners) l();
}
